import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnChanges, SimpleChanges, forwardRef, inject, input, output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';

import { ComissaoEstruturaFiltroResponse, ComissaoTipoComissaoResponse } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type TipoFiltroArvoreComissao = 'nome' | 'codigo';
type StatusFiltroArvoreComissao = 'todos' | 'ativas' | 'extintas';

interface ArvoreComissaoNo {
    id: number;
    idEoSuperior: number | null;
    idTipoComissaoCt: number;
    descricao: string;
    ativo: number;
    filhos: ArvoreComissaoNo[];
}

interface ArvoreComissaoTipo {
    idTipoComissaoCt: number;
    descricao: string;
    filhos: ArvoreComissaoNo[];
}

@Component({
    selector: 'app-seletor-arvore-comissao',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, ButtonModule, DialogModule, InputTextModule],
    templateUrl: './seletor-arvore-comissao.html',
    styleUrls: ['./seletor-arvore-comissao.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SeletorArvoreComissaoComponent),
            multi: true
        }
    ]
})
/**
 * Campo (ControlValueAccessor) que seleciona uma estrutura de comissão/CT/GT numa árvore por
 * tipo, com filtro por nome/código e status (ativas/extintas/todas).
 */
export class SeletorArvoreComissaoComponent implements ControlValueAccessor, OnChanges {
    private readonly cdr = inject(ChangeDetectorRef);

    /** Tipos de comissão (raízes da árvore). */
    readonly tiposComissao = input<ComissaoTipoComissaoResponse[]>([]);
    /** Estruturas organizacionais (nós da árvore). */
    readonly estruturasComissao = input<ComissaoEstruturaFiltroResponse[]>([]);
    /** Restringe a árvore a um único tipo de comissão (opcional). */
    readonly idTipoComissaoCt = input<number | null>(null);
    /** Exibe estado de carregamento no diálogo. */
    readonly carregando = input(false);
    /** Mensagem de erro ao carregar as estruturas. */
    readonly erro = input('');
    /** Texto do campo quando nada está selecionado. */
    readonly placeholder = input('Selecionar Comissão');
    /** Título do diálogo de seleção. */
    readonly tituloDialogo = input('Localizar Comissão / Câmara Técnica / Núcleo');
    /** Emite a estrutura escolhida ao confirmar. */
    readonly estruturaSelecionada = output<ComissaoEstruturaFiltroResponse>();

    valor: number | null = null;
    desabilitado = false;
    exibirDialog = false;
    termoFiltro = '';
    tipoFiltro: TipoFiltroArvoreComissao = 'nome';
    statusFiltro: StatusFiltroArvoreComissao = 'ativas';
    noSelecionado: ArvoreComissaoNo | null = null;
    idsTiposExpandidos = new Set<number>();
    idsNosExpandidos = new Set<number>();
    arvore: ArvoreComissaoTipo[] = [];

    private aoMudar: (valor: number | null) => void = () => undefined;
    private aoTocar: () => void = () => undefined;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tiposComissao'] || changes['estruturasComissao'] || changes['idTipoComissaoCt']) {
            this.arvore = this.montarArvore();
            this.expandirRaizes();
            this.sincronizarNoSelecionado();
        }
    }

    writeValue(valor: number | null): void {
        this.valor = this.normalizarIdPositivo(valor);
        this.sincronizarNoSelecionado();
        // CVA sob OnPush: o valor pode chegar fora de um ciclo de CD (patch assíncrono do form).
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (valor: number | null) => void): void {
        this.aoMudar = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.aoTocar = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.desabilitado = isDisabled;
    }

    get descricaoSelecionada(): string {
        if (!this.valor) {
            return '';
        }

        const estrutura = this.estruturasComissao().find((item) => Number(item.idEstruturaOrganizacional ?? 0) === this.valor);
        if (!estrutura) {
            return String(this.valor);
        }

        const descricao = (estrutura.dsEstruturaOrganizacional ?? '').trim();
        return `${this.valor} - ${descricao || 'Estrutura'}`;
    }

    get tiposExibicao(): ArvoreComissaoTipo[] {
        return this.filtrarTiposArvore();
    }

    abrirDialogo(): void {
        if (this.desabilitado) {
            return;
        }
        this.termoFiltro = '';
        this.tipoFiltro = 'nome';
        this.statusFiltro = 'ativas';
        this.sincronizarNoSelecionado();
        this.exibirDialog = true;
        this.expandirRaizes();
        this.aoTocar();
    }

    cancelar(): void {
        this.exibirDialog = false;
        this.aoTocar();
    }

    onDialogHide(): void {
        this.aoTocar();
    }

    confirmar(): void {
        if (!this.noSelecionado) {
            return;
        }

        this.valor = this.noSelecionado.id;
        this.aoMudar(this.valor);
        this.aoTocar();
        this.exibirDialog = false;

        const estrutura = this.estruturasComissao().find((item) => Number(item.idEstruturaOrganizacional ?? 0) === this.valor);
        if (estrutura) {
            this.estruturaSelecionada.emit(estrutura);
        }
    }

    limpar(): void {
        this.valor = null;
        this.noSelecionado = null;
        this.aoMudar(null);
        this.aoTocar();
    }

    onFiltroChange(valor: string): void {
        this.termoFiltro = this.tipoFiltro === 'codigo' ? (valor || '').replace(/\D/g, '') : (valor || '').toUpperCase();
        this.expandirRaizes();
    }

    alternarTipo(idTipo: number): void {
        this.alternarSet(this.idsTiposExpandidos, idTipo);
    }

    tipoExpandido(idTipo: number): boolean {
        return this.idsTiposExpandidos.has(idTipo);
    }

    alternarNo(id: number): void {
        this.alternarSet(this.idsNosExpandidos, id);
    }

    noExpandido(id: number): boolean {
        return this.idsNosExpandidos.has(id);
    }

    selecionarNo(no: ArvoreComissaoNo): void {
        this.noSelecionado = no;
    }

    trackTipo(_: number, tipo: ArvoreComissaoTipo): number {
        return tipo.idTipoComissaoCt;
    }

    trackNo(_: number, no: ArvoreComissaoNo): number {
        return no.id;
    }

    private montarArvore(): ArvoreComissaoTipo[] {
        const tiposPorId = new Map<number, ArvoreComissaoTipo>();
        this.tiposComissao().forEach((tipo) => {
            const idTipo = this.normalizarIdPositivo(tipo.idTipoComissaoCt);
            if (idTipo === null || !this.tipoPermitido(idTipo)) {
                return;
            }

            tiposPorId.set(idTipo, {
                idTipoComissaoCt: idTipo,
                descricao: tipo.dsTipoComissaoCt || `Tipo ${idTipo}`,
                filhos: []
            });
        });

        const nosPorId = new Map<number, ArvoreComissaoNo>();
        const nosPorTipo = new Map<number, ArvoreComissaoNo[]>();

        this.estruturasComissao().forEach((estrutura) => {
            const id = this.normalizarIdPositivo(estrutura.idEstruturaOrganizacional);
            const idTipo = this.normalizarIdPositivo(estrutura.idTipoComissaoCt);
            if (id === null || idTipo === null || !this.tipoPermitido(idTipo)) {
                return;
            }

            if (!tiposPorId.has(idTipo)) {
                tiposPorId.set(idTipo, {
                    idTipoComissaoCt: idTipo,
                    descricao: `Tipo ${idTipo}`,
                    filhos: []
                });
            }

            const no: ArvoreComissaoNo = {
                id,
                idEoSuperior: this.normalizarIdPositivo(estrutura.idEoSuperior),
                idTipoComissaoCt: idTipo,
                descricao: estrutura.dsEstruturaOrganizacional || `Estrutura ${id}`,
                ativo: estrutura.ativo === 0 ? 0 : 1,
                filhos: []
            };

            nosPorId.set(id, no);
            const listaTipo = nosPorTipo.get(idTipo) || [];
            listaTipo.push(no);
            nosPorTipo.set(idTipo, listaTipo);
        });

        tiposPorId.forEach((tipo) => {
            const nosDoTipo = nosPorTipo.get(tipo.idTipoComissaoCt) || [];
            nosDoTipo.forEach((no) => {
                const pai = no.idEoSuperior ? nosPorId.get(no.idEoSuperior) : null;
                if (pai && pai.idTipoComissaoCt === no.idTipoComissaoCt && nosDoTipo.includes(pai)) {
                    pai.filhos.push(no);
                } else {
                    tipo.filhos.push(no);
                }
            });
            this.ordenarNos(tipo.filhos);
        });

        return Array.from(tiposPorId.values())
            .filter((tipo) => tipo.filhos.length > 0)
            .sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR'));
    }

    private tipoPermitido(idTipo: number): boolean {
        const tipoAtual = this.normalizarIdPositivo(this.idTipoComissaoCt());
        return tipoAtual === null || idTipo === tipoAtual;
    }

    private ordenarNos(nos: ArvoreComissaoNo[]): void {
        nos.sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR'));
        nos.forEach((no) => this.ordenarNos(no.filhos));
    }

    private filtrarTiposArvore(): ArvoreComissaoTipo[] {
        return this.arvore
            .map((tipo) => ({
                ...tipo,
                filhos: this.filtrarNos(tipo.filhos)
            }))
            .filter((tipo) => tipo.filhos.length > 0 || !this.termoFiltro.trim());
    }

    private filtrarNos(nos: ArvoreComissaoNo[]): ArvoreComissaoNo[] {
        const busca = this.normalizarTexto(this.termoFiltro);
        return nos.reduce<ArvoreComissaoNo[]>((acc, no) => {
            const filhosFiltrados = this.filtrarNos(no.filhos);
            const statusValido = this.statusNoValido(no);
            const termoValido = !busca || (this.tipoFiltro === 'codigo' ? String(no.id).includes(busca) : this.normalizarTexto(no.descricao).includes(busca));

            if (statusValido && termoValido) {
                acc.push({ ...no, filhos: filhosFiltrados });
            } else if (filhosFiltrados.length > 0) {
                acc.push(...filhosFiltrados);
            }

            return acc;
        }, []);
    }

    private statusNoValido(no: ArvoreComissaoNo): boolean {
        if (this.statusFiltro === 'ativas') {
            return no.ativo === 1;
        }
        if (this.statusFiltro === 'extintas') {
            return no.ativo === 0;
        }
        return true;
    }

    private sincronizarNoSelecionado(): void {
        if (!this.valor) {
            this.noSelecionado = null;
            return;
        }

        this.noSelecionado = this.localizarNo(this.arvore, this.valor);
    }

    private localizarNo(tipos: ArvoreComissaoTipo[], id: number): ArvoreComissaoNo | null {
        for (const tipo of tipos) {
            const no = this.localizarNoFilhos(tipo.filhos, id);
            if (no) {
                return no;
            }
        }
        return null;
    }

    private localizarNoFilhos(nos: ArvoreComissaoNo[], id: number): ArvoreComissaoNo | null {
        for (const no of nos) {
            if (no.id === id) {
                return no;
            }
            const filho = this.localizarNoFilhos(no.filhos, id);
            if (filho) {
                return filho;
            }
        }
        return null;
    }

    private expandirRaizes(): void {
        this.idsTiposExpandidos = new Set(this.arvore.map((tipo) => tipo.idTipoComissaoCt));
        if (this.termoFiltro.trim()) {
            this.idsNosExpandidos = new Set(this.coletarIdsComFilhos(this.tiposExibicao));
        }
    }

    private coletarIdsComFilhos(tipos: ArvoreComissaoTipo[]): number[] {
        return tipos.flatMap((tipo) => this.coletarIdsNosComFilhos(tipo.filhos));
    }

    private coletarIdsNosComFilhos(nos: ArvoreComissaoNo[]): number[] {
        return nos.flatMap((no) => [...(no.filhos.length > 0 ? [no.id] : []), ...this.coletarIdsNosComFilhos(no.filhos)]);
    }

    private alternarSet(set: Set<number>, id: number): void {
        if (set.has(id)) {
            set.delete(id);
        } else {
            set.add(id);
        }
    }

    private normalizarIdPositivo(valor: number | null | undefined): number | null {
        const id = Number(valor);
        return Number.isFinite(id) && id > 0 ? id : null;
    }

    private normalizarTexto(valor: string): string {
        return (
            valor
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                /* H\u00e1 nomes gravados com espa\u00e7o duplo; o HTML colapsa na exibi\u00e7\u00e3o, ent\u00e3o o usu\u00e1rio
               copia da tela um termo que nunca casaria com o valor cru. */
                .replace(/\s+/g, ' ')
                .trim()
        );
    }
}
